/**
 * Turns what a person dropped or picked into a flat list of `{ file, relPath, size }` with every subfolder kept.
 *
 * Drops use the File and Directory Entries API (`DataTransferItem.webkitGetAsEntry`), which walks dropped folders
 * recursively; a browser without it falls back to `dataTransfer.files` (folders then cannot be read, only files).
 * The folder picker (`<input webkitdirectory>`) gives `webkitRelativePath` on each File.
 */

/** All entries of a directory: `readEntries` hands them out in batches (100 in Chrome) until it returns none. */
function readAllEntries(directoryEntry) {
  const reader = directoryEntry.createReader();
  const all = [];
  return new Promise((resolve, reject) => {
    const next = () => reader.readEntries((batch) => {
      if (!batch.length) { resolve(all); return; }
      all.push(...batch);
      next();
    }, reject);
    next();
  });
}

const fileOf = (fileEntry) => new Promise((resolve, reject) => fileEntry.file(resolve, reject));

/** Walks one FileSystemEntry; files land in `out.files`, folders with nothing inside in `out.emptyFolders`. */
async function walkEntry(entry, prefix, out) {
  const relPath = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile) {
    try {
      const file = await fileOf(entry);
      out.files.push({ file, relPath, size: file.size });
    } catch (error) {
      out.unreadable.push({ relPath, error: error?.message || 'unreadable' });
    }
    return;
  }
  if (entry.isDirectory) {
    out.folderNames.push(relPath);
    const children = await readAllEntries(entry);
    if (!children.length) { out.emptyFolders.push(relPath); }
    for (const child of children) {
      // eslint-disable-next-line no-await-in-loop
      await walkEntry(child, relPath, out);
    }
  }
}

/** True when the drag carries files from the OS (not an item dragged inside the tree). */
export function isOsFileDrag(dataTransfer) {
  return Boolean(dataTransfer && Array.from(dataTransfer.types || []).includes('Files'));
}

/**
 * Must be called synchronously inside the drop handler: the entries are only readable during the event, so they are
 * taken first and walked afterwards.
 * @returns {Promise<{files: Array<{file: File, relPath: string, size: number}>, emptyFolders: string[], folderNames: string[], unreadable: Array}>}
 */
export function collectFromDataTransfer(dataTransfer) {
  const items = Array.from(dataTransfer?.items || []).filter((item) => item.kind === 'file');
  const entries = items.map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null));
  const out = { files: [], emptyFolders: [], folderNames: [], unreadable: [] };
  if (entries.length && entries.every(Boolean)) {
    return entries.reduce((chain, entry) => chain.then(() => walkEntry(entry, '', out)), Promise.resolve()).then(() => out);
  }
  // No Entries API (or not for every item): the plain file list. A dropped folder shows up as a zero-byte "file"
  // with no type in some browsers; those are left out rather than uploaded as empty files.
  for (const file of Array.from(dataTransfer?.files || [])) {
    const relPath = file.webkitRelativePath || file.name;
    if (!file.type && file.size === 0 && !/\.[^/]+$/.test(file.name)) {
      out.unreadable.push({ relPath, error: 'folders cannot be read by this browser; use "Upload folder…"' });
      continue;
    }
    out.files.push({ file, relPath, size: file.size });
  }
  return Promise.resolve(out);
}

/** The files of an `<input type="file">` (with `webkitdirectory`: every file of the folder, relative paths kept). */
export function collectFromFileList(fileList) {
  const files = Array.from(fileList || []).map((file) => ({ file, relPath: file.webkitRelativePath || file.name, size: file.size }));
  const folderNames = new Set();
  for (const { relPath } of files) {
    const segments = relPath.split('/');
    for (let index = 1; index < segments.length; index += 1) { folderNames.add(segments.slice(0, index).join('/')); }
  }
  return { files, emptyFolders: [], folderNames: [...folderNames], unreadable: [] };
}
